import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import StructuredData, { breadcrumbSchema } from "../components/StructuredData";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

export default function ReturnPolicy() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Name, email, and message are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="return-policy-page"
      className="relative min-h-[80vh]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.94), rgba(249,247,243,0.98)), url(${POWDER})`,
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
      }}
    >
      <SEO
        title="Return Policy & Contact — BrushBasics"
        description="BrushBasics operates a no-returns policy on all physical and digital purchases. Reach us directly through the contact form for any questions."
        path="/return-policy"
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Return Policy & Contact", path: "/return-policy" },
          ]),
        ]}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <Link
          to="/"
          className="text-xs uppercase tracking-[0.22em] text-stone-500 hover:text-[#8C6A5D] transition-colors"
        >
          ← Home
        </Link>

        <header className="mt-8 mb-12">
          <div className="overline mb-3">Volume 05 · Policy</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
            Return
            <br />
            <em className="italic text-stone-700">Policy.</em>
          </h1>
        </header>

        {/* Policy content */}
        <div className="bg-white/85 border border-[#E5E0D8] p-8 lg:p-12">
          <div className="overline mb-3 text-[#8C6A5D]">All Sales Final</div>
          <h2 className="font-serif-display text-2xl sm:text-3xl text-stone-900 font-light leading-tight">
            BrushBasics does not offer returns
            <br />
            <em className="italic text-stone-700">on any purchase.</em>
          </h2>

          <div className="mt-8 space-y-6 text-base leading-relaxed text-stone-700">
            <p>
              Every purchase from BrushBasics — physical brushes, brush bundles,
              digital courses, guide access, and AI tool credits — is
              <span className="text-stone-900"> final</span>. We do not accept
              returns, exchanges, or refund requests.
            </p>
            <p>
              This policy applies to all products:
            </p>
            <ul className="list-disc list-outside ml-6 space-y-2 text-stone-700">
              <li>Physical makeup brushes and brush bundles</li>
              <li>Tutorial Vault access ($29)</li>
              <li>Brush ID AI tool access ($9.99)</li>
              <li>Look Builder AI generations ($4.99 each)</li>
              <li>30 Brushes in 30 Days email course ($19.99)</li>
              <li>Any other current or future product on the site</li>
            </ul>
            <p>
              For hygiene reasons, used makeup brushes cannot be returned. For
              the same reason we cannot resell items even if unused. Digital
              products are non-refundable once access is granted.
            </p>
            <p className="text-stone-600 border-l-2 border-[#8C6A5D] pl-5 py-1 italic">
              We recommend previewing the site thoroughly, taking the free
              Brush Finder Quiz, and reading the free guide before making any
              purchase. Every question is welcome before you buy — please use
              the contact form below.
            </p>
            <div className="pt-4 border-t border-[#E5E0D8]">
              <div className="overline mb-2">Damaged or wrong item</div>
              <p className="text-stone-600 text-sm leading-relaxed">
                In the rare case that a physical order arrives visibly damaged
                or you receive the wrong item, please contact us{" "}
                <span className="text-stone-900">within 7 days of delivery</span>{" "}
                with a photo. We will send a replacement at our discretion.
                This does not constitute a return — the original item is
                yours to keep.
              </p>
            </div>
          </div>
        </div>

        {/* Contact form */}
        <section className="mt-16" id="contact">
          <div className="overline mb-3">Get in Touch</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 font-light leading-tight">
            Questions before you buy?
            <br />
            <em className="italic text-stone-700">Write to us.</em>
          </h2>
          <p className="mt-4 text-sm text-stone-600 leading-relaxed max-w-xl">
            Every message is read personally, and we typically reply within
            two business days.
          </p>

          {sent ? (
            <div
              data-testid="contact-success"
              className="mt-8 bg-white border-l-4 border-[#8C6A5D] border-y border-r border-y-[#E5E0D8] border-r-[#E5E0D8] p-6"
            >
              <div className="overline mb-2 text-[#8C6A5D]">Message received</div>
              <p className="text-sm text-stone-700">
                Thank you — we&apos;ll be in touch shortly at the email you
                provided. In the meantime, feel free to{" "}
                <Link to="/catalog" className="text-[#8C6A5D] underline underline-offset-4">
                  browse the catalog
                </Link>{" "}
                or{" "}
                <Link to="/quiz" className="text-[#8C6A5D] underline underline-offset-4">
                  take the Brush Finder Quiz
                </Link>
                .
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
                className="mt-4 text-xs uppercase tracking-[0.22em] text-stone-600 hover:text-[#8C6A5D] transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="mt-8 bg-white border border-[#E5E0D8] p-6 lg:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="overline block mb-2" htmlFor="contact-name">Your name</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    required
                    className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
                  />
                </div>
                <div>
                  <label className="overline block mb-2" htmlFor="contact-email">Email</label>
                  <input
                    id="contact-email"
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    required
                    className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-2" htmlFor="contact-subject">Subject (optional)</label>
                <input
                  id="contact-subject"
                  data-testid="contact-subject"
                  type="text"
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="e.g. Question about the Powder Brush"
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5"
                />
              </div>

              <div>
                <label className="overline block mb-2" htmlFor="contact-message">Your message</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message"
                  value={form.message}
                  onChange={set("message")}
                  required
                  rows={6}
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-2.5 resize-y leading-relaxed"
                />
              </div>

              {error && (
                <div data-testid="contact-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3">
                  {error}
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-stone-500 leading-relaxed max-w-xs">
                  By submitting, you agree we may reply to your email. We do
                  not add you to any list.
                </p>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={sending}
                  className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-400 text-white px-8 py-3.5 transition-colors"
                >
                  {sending ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
